import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

export interface QarStdDescEditDialogData {
  rowData?: any;
  stdDescList?: any[];
  generalConfig?: any;
}

@Component({
  selector: 'gtw-qar-std-desc-edit',
  templateUrl: './qar-std-desc-edit.component.html',
  styleUrls: ['./qar-std-desc-edit.component.scss'],
  standalone: false,
})
export class QarStdDescEditComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);
  private readonly gtwSnackbarService = inject(GtwSnackbarService);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly dialogRef = inject(MatDialogRef<QarStdDescEditComponent>, { optional: true });
  private readonly data = inject<QarStdDescEditDialogData>(MAT_DIALOG_DATA, { optional: true });

  isEditMode = false;
  editorTitle = 'Add New Standard Description';
  stdDesc = '';
  selectedSystemFlag = 'N';
  saving = false;
  minimise = false;
  systemFlag = ['Y', 'N'];
  activeStdDescId: number | null = null;
  stdDescList: any[] = [];
  descTouched = false;

  private originalStdDesc = '';
  private originalSystemFlag = 'N';
  private readonly generalConfig: any;

  constructor() {
    this.stdDescList = this.data?.stdDescList || [];
    this.generalConfig = this.data?.generalConfig || {};

    const row = this.data?.rowData;
    const rowId = row?.STD_DESC_ID ?? row?.std_desc_id ?? null;
    if (row && rowId != null) {
      this.isEditMode = true;
      this.editorTitle = 'Edit Standard Description';
      this.activeStdDescId = rowId;
      this.stdDesc = row?.QAR_DESC ?? row?.STD_DESC ?? row?.description ?? '';
      this.selectedSystemFlag = row?.SYSTEM_FLAG ?? row?.system_flag ?? 'N';
    } else {
      this.isEditMode = false;
      this.editorTitle = 'Add New Standard Description';
      this.stdDesc = '';
      this.selectedSystemFlag = 'N';
    }
    this.originalStdDesc = this.stdDesc;
    this.originalSystemFlag = this.selectedSystemFlag;
  }

  resetForm(): void {
    if (this.isEditMode) {
      this.stdDesc = this.originalStdDesc;
      this.selectedSystemFlag = this.originalSystemFlag;
      this.descTouched = false;
    } else {
      this.stdDesc = '';
      this.selectedSystemFlag = 'N';
    }
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  get isDuplicateDesc(): boolean {
    const value = this.stdDesc || '';
    if (!value) {
      return false;
    }
    if (this.isEditMode && value === this.originalStdDesc) {
      return false;
    }
    return this.stdDescList.some((row: any) => {
      const desc = row?.QAR_DESC ?? row?.STD_DESC ?? row?.description ?? '';
      return desc === value;
    });
  }

  get canSave(): boolean {
    const value = (this.stdDesc || '').trim();
    if (!value || this.isDuplicateDesc || this.saving) {
      return false;
    }
    if (!this.isEditMode) {
      return true;
    }
    return value !== (this.originalStdDesc || '').trim() || this.selectedSystemFlag !== this.originalSystemFlag;
  }

  save(): void {
    const value = (this.stdDesc || '').trim();
    if (!value) {
      this.gtwSnackbarService.openSnackBar('QAR Standard Description is required.', 'gtw-snackbar--yellow');
      return;
    }
    if (this.isDuplicateDesc) {
      this.gtwSnackbarService.openSnackBar('QAR Standard Description must be unique.', 'gtw-snackbar--yellow');
      return;
    }

    this.saving = true;
    const baseUrl = this.generalConfig?.base_url || '';
    if (!baseUrl) {
      this.saving = false;
      this.gtwSnackbarService.openSnackBar('Base URL is unavailable. Please refresh and try again.', 'gtw-snackbar--red');
      return;
    }

    const payload = {
      std_desc: value,
      system_flag: this.selectedSystemFlag || 'N',
      ...(this.isEditMode ? { std_desc_id: this.activeStdDescId } : {}),
    };
    const url = this.isEditMode ? `${baseUrl}/updateStdDesc` : `${baseUrl}/createStdDesc`;
    const successMessage = this.isEditMode
      ? 'Standard Description Updated Successfully!'
      : 'Standard Description added Successfully!';

    this.http
      .post<any>(url, payload, { withCredentials: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => this.handleSaveResponse(response, successMessage),
        () => this.handleSaveError()
      );
  }

  private handleSaveResponse(response: any, successMessage: string): void {
    this.saving = false;
    const data = response?.data || response;
    if (data?.callSuccess === '0') {
      this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Error occurred while saving.', 'gtw-snackbar--red');
      this.cdr.detectChanges();
      return;
    }
    this.gtwSnackbarService.openSnackBar(successMessage, 'gtw-snackbar--green');
    this.dialogRef?.close('saved');
  }

  private handleSaveError(): void {
    this.saving = false;
    this.gtwSnackbarService.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
    this.cdr.detectChanges();
  }
}
